import useCrmContext from 'hooks/useCrmContext';
import useTasksChanged from 'hooks/useTasksChanged';
import { useCallback, useEffect, useRef, useState } from 'react';
import crmApi from 'server/crm/crmApi';

/**
 * One page of the comments that name the signed-in user, the latest first
 * (server/crm/crmApi `getMentions`), read again when the page, the page size
 * or the filter change, and whenever the tasks change: a task opened reads
 * its mentions. `mentions` is null while a page is read; `counts` keeps the
 * last ones received so that the tabs do not blink; `me` names the user in
 * their text.
 */
const useMentions = ({ page = 1, pageSize = 20, filter = 'all' } = {}) => {
  const { call, me, ready } = useCrmContext();
  const [result, setResult] = useState({
    mentions: null,
    total: 0,
    totalPages: 0,
  });
  const [counts, setCounts] = useState(null);
  const [failed, setFailed] = useState(false);
  // Fast clicks on the pages: only the answer of the latest call is kept.
  const latest = useRef(0);

  const load = useCallback(() => {
    if (!ready) return;
    const request = ++latest.current;
    setResult((previous) => ({ ...previous, mentions: null }));
    call(crmApi.getMentions, { page, pageSize, filter }).then(
      (response) => {
        if (request !== latest.current) return;
        setResult({
          mentions: response.data,
          total: response.total,
          totalPages: response.totalPages,
        });
        setCounts(response.counts);
        setFailed(false);
      },
      () => {
        if (request === latest.current) setFailed(true);
      },
    );
  }, [call, ready, page, pageSize, filter]);
  useEffect(load, [load]);
  useTasksChanged(load);

  return { ...result, counts, failed, me, reload: load };
};

export default useMentions;
